import type { LucideIcon } from 'lucide-react';
import {
  Package,
  FileText,
  Bell,
  Users,
  Receipt,
  AlertTriangleIcon,
  ShoppingBag,
  Building2,
  Tags,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
} from '@/components/ui/sidebar';
import { useMemo } from 'react';
import { useUser } from '@/contexts/UserContext';
import { UserMenu } from '@/components/auth/UserMenu';

interface SidebarGroupDefinition {
  label: string;
  items: Array<{
    title: string;
    url: string;
    icon: LucideIcon;
    match?: (pathname: string) => boolean;
  }>;
}

/**
 * Application sidebar with navigation and key actions.
 */
export function AppSidebar() {
  const location = useLocation();
  const { isServiceAdmin } = useUser();

  const menuGroups = useMemo<SidebarGroupDefinition[]>(() => {
    const groups: SidebarGroupDefinition[] = [];

    if (isServiceAdmin) {
      groups.push({
        label: 'Service Administration',
        items: [
          {
            title: 'Products',
            url: '/dashboard/admin/products',
            icon: Package,
            match: (pathname: string) => pathname.startsWith('/dashboard/admin/products'),
          },
          {
            title: 'Feature Catalog',
            url: '/dashboard/admin/feature-catalog',
            icon: Tags,
            match: (pathname: string) => pathname.startsWith('/dashboard/admin/feature-catalog'),
          },
          {
            title: 'Organizations',
            url: '/dashboard/admin/organizations',
            icon: Building2,
            match: (pathname: string) => pathname.startsWith('/dashboard/admin/organizations'),
          },
          {
            title: 'Legal Documents',
            url: '/dashboard/admin/legal-documents',
            icon: FileText,
            match: (pathname: string) => pathname.startsWith('/dashboard/admin/legal-documents'),
          },
          {
            title: 'System Notices',
            url: '/dashboard/admin/system-notices',
            icon: AlertTriangleIcon,
            match: (pathname: string) => pathname.startsWith('/dashboard/admin/system-notices'),
          },
          {
            title: 'Notifications',
            url: '/dashboard/admin/notifications/templates',
            icon: Bell,
            match: (pathname: string) => pathname.startsWith('/dashboard/admin/notifications'),
          },
        ],
      });

      groups.push({
        label: 'Stripe',
        items: [
          {
            title: 'Products',
            url: '/dashboard/admin/stripe/products',
            icon: ShoppingBag,
            match: (pathname: string) => pathname === '/dashboard/admin/stripe/products',
          },
          {
            title: 'Customers',
            url: '/dashboard/admin/stripe/customers',
            icon: Users,
            match: (pathname: string) => pathname === '/dashboard/admin/stripe/customers',
          },
          {
            title: 'Subscriptions',
            url: '/dashboard/admin/stripe/subscriptions',
            icon: Receipt,
            match: (pathname: string) => pathname === '/dashboard/admin/stripe/subscriptions',
          },
        ],
      });
    }

    return groups;
  }, [isServiceAdmin]);

  return (
    <>
      <Sidebar>
        <SidebarHeader className="border-b px-4 py-3">
          <h1>Mission Impact Admin</h1>
        </SidebarHeader>
        <SidebarContent>
          {menuGroups.map((group) => (
            <SidebarGroup key={group.label}>
              <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {group.items.map((item) => {
                    const isActive = item.match
                      ? item.match(location.pathname)
                      : location.pathname === item.url;

                    return (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton asChild isActive={isActive}>
                          <Link to={item.url}>
                            <item.icon className="h-4 w-4" />
                            <span>{item.title}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ))}
        </SidebarContent>
        <SidebarFooter className="border-t p-4">
          <UserMenu />
        </SidebarFooter>
      </Sidebar>
    </>
  );
}
